import { z } from 'zod';
import { ClientMemberAttributeConfigurationDto } from './clientMemberAttributeConfiguration.dto';
import { InternalPhoneNumberDto } from './internalPhoneNumber.dto';

export interface ClientConsentTerm {
  id: number;
  name: string;
  key: string;
}

export interface ClientInternalNumbersDto extends InternalPhoneNumberDto {
  type: 'feedbackNumber' | 'phoneNumber';
}

export interface ClientDto {
  id: number;
  name: string;
  displayName?: string;
  findHelpApiKey?: string;
  avonMedicalCenterId?: string;
  clientMarketNumbers?: ClientInternalNumbersDto[];
  clientConfiguration?: ClientMemberAttributeConfigurationDto;
  callEngagementLengthRequirementInMinutes?: number;
  teamIds: number[];
  terms?: ClientConsentTerm[];
  reemaWebsite?: string;
  smsReEngagementThreshold?: number;
  averageMonthsInProgram?: number;
  hasADTFeed?: boolean;
  isActive?: boolean;
}

export interface ClientDetails {
  id: number;
  name: string;
  displayName?: string;
  findHelpApiKey?: string;
  avonMedicalCenterId?: string;
  feedbackNumberId: string;
  internalPhoneNumberId: string;
  callEngagementLengthRequirementInMinutes?: number;
  reemaWebsite?: string;
  terms?: ClientConsentTerm[];
  smsReEngagementThreshold?: number;
  teamIds: number[];
}

export interface TrainingClientProviderStatusDto {
  isProviderClient: boolean;
}

export const CreateClientSchema = z
  .object({
    name: z.string().nonempty('required'),
    displayName: z.string().nonempty('required'),
    avonMedicalCenterId: z.string().optional(),
    reemaWebsite: z.string(),
    callEngagementLengthRequirementInMinutes: z.coerce.number({
      invalid_type_error: 'Required',
    }),
    smsReEngagementThreshold: z.coerce.number({
      invalid_type_error: 'Required',
    }),
    terms: z.number().array().optional(),
    feedbackNumberId: z.string().optional(),
    internalPhoneNumberId: z.string().optional(),
    teamIds: z.number().array().optional(),
    hasADTFeed: z.boolean().optional(),
  })
  .strict();

export const UpdateClientSchema = z
  .object({
    name: z.string().optional(),
    displayName: z.string().optional(),
    findHelpApiKey: z.string().optional(),
    avonMedicalCenterId: z.string().optional(),
    reemaWebsite: z.string().optional(),
    feedbackNumberId: z.string().optional(),
    internalPhoneNumberId: z.string().optional(),
    callEngagementLengthRequirementInMinutes: z.coerce.number().optional(),
    smsReEngagementThreshold: z.coerce.number().optional(),
    terms: z.number().array().optional(),
    teamIds: z.number().array().optional(),
    hasADTFeed: z.boolean().optional(),
  })
  .strict();

export type CreateClientRequest = z.infer<typeof CreateClientSchema>;

export type UpdateClientRequest = z.infer<typeof UpdateClientSchema>;

export type ClientDetailsDto = {
  id: number;
  name: string;
  displayName: string;
  website: string;
  marketNumber: {
    id: string;
    displayName: string;
    number: string;
    supportUserIds: string[];
  };
  feedbackNumber: {
    id: string;
    displayName: string;
    number: string;
    supportUserIds: string[];
  };
  avonMedicalCenterId: string;
  smsReEngagementThreshold: number;
  callEngagementLengthRequirementInMinutes: number;
  averageMonthsInProgram: number;
  terms: {
    id: number;
    name: string;
    key: string;
  }[];
  teams: Array<{
    id: number;
    name: string;
    users: Array<{
      id: string;
      isManager: boolean;
      firstName: string;
      lastName: string;
      phoneNumber: string;
      resourcingNumber: string;
    }>;
  }>;
};
